import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, Briefcase, CheckCircle, Award, Clock, MapPin, Phone, Mail } from 'lucide-react';
import api from '../api';

const EmployerSignup = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'TN',
    zip_code: '',
    industry: 'healthcare',
    company_size: '',
    hiring_needs: '',
    ojt_interest: true,
    post_training_interest: true,
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.post('/employers/register', formData);
      setSubmitted(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ maxWidth: '600px', textAlign: 'center' }}>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            background: 'var(--success)', 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <CheckCircle size={40} color="white" />
          </div>
          <h2 style={{ marginBottom: '16px' }}>Thank You for Your Interest!</h2>
          <p style={{ color: 'var(--gray-600)', marginBottom: '24px' }}>
            Your employer registration has been submitted successfully. Our team will review your 
            information and contact you within 2-3 business days to discuss partnership opportunities.
          </p>
          <Link to="/" className="btn btn-primary">Return to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="employer-page" data-testid="employer-signup-page">
      {/* Header */}
      <div className="gov-header">
        Tennessee Department of Labor &amp; Workforce Development Registered Training Provider
      </div>
      
      <nav className="nav-bar">
        <Link to="/" className="nav-logo">
          <div className="nav-logo-icon">MC</div>
          <div className="nav-logo-text">
            <span className="nav-logo-title">MedCare Training Academy</span>
            <span className="nav-logo-subtitle">Apprenticeship Program</span>
          </div>
        </Link>
        <div className="nav-links">
          <Link to="/#programs" className="nav-link">Programs</Link>
          <Link to="/partner" className="nav-link">Become a Partner</Link>
          <Link to="/login" className="btn btn-primary btn-sm">Student Portal</Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero" style={{ paddingBottom: '40px' }}>
        <div className="hero-badge" style={{ background: 'var(--success)' }}>
          <Building2 size={16} />
          Employer Partnership Program
        </div>
        <h1>Hire Skilled Healthcare<br />Professionals</h1>
        <p className="tagline">
          Partner with MedCare Training Academy to access a pipeline of trained, certified 
          healthcare professionals ready for On-the-Job Training (OJT) or immediate employment.
        </p>
      </section>

      {/* Benefits Section */}
      <section className="features-section" style={{ paddingTop: '40px' }}>
        <h2 className="section-title">Why Partner With Us?</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon" style={{ background: 'var(--success)' }}>
              <Users size={28} />
            </div>
            <h3>Qualified Candidates</h3>
            <p>Access pre-screened, trained CMA and PN graduates ready to contribute from day one.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon" style={{ background: 'var(--primary-blue)' }}>
              <Briefcase size={28} />
            </div>
            <h3>OJT Opportunities</h3>
            <p>Participate in On-the-Job Training programs with potential grant funding support.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon" style={{ background: '#7c3aed' }}>
              <Award size={28} />
            </div>
            <h3>State Certified</h3>
            <p>Our graduates are prepared for TN state certification exams with high pass rates.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon" style={{ background: '#ea580c' }}>
              <Clock size={28} />
            </div>
            <h3>Flexible Hiring</h3>
            <p>Hire during training (OJT) or after completion based on your facility needs.</p>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="info-section" style={{ background: 'var(--off-white)' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 className="section-title">Employer Registration</h2>
          <p className="section-subtitle">
            Complete the form below to register as an employer partner. Our team will contact you 
            to discuss hiring opportunities and partnership details.
          </p>

          <form onSubmit={handleSubmit} className="card" style={{ padding: '32px' }}>
            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
              {/* Company Information */}
              <div style={{ gridColumn: '1 / -1' }}>
                <h3 style={{ marginBottom: '16px', color: 'var(--primary-blue)' }}>Company Information</h3>
              </div>
              
              <div className="form-group">
                <label className="form-label">Company/Facility Name *</label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter company name"
                  required
                  data-testid="company-name-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Industry Type *</label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className="form-input"
                  required
                  data-testid="industry-select"
                >
                  <option value="healthcare">Healthcare Facility</option>
                  <option value="hospital">Hospital</option>
                  <option value="nursing_home">Nursing Home</option>
                  <option value="assisted_living">Assisted Living</option>
                  <option value="home_health">Home Health Agency</option>
                  <option value="clinic">Medical Clinic</option>
                  <option value="other">Other Healthcare</option>
                </select>
              </div>

              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Company Address *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Street address"
                  required
                  data-testid="address-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="City"
                  required
                  data-testid="city-input"
                />
              </div>

              <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label className="form-label">State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="form-input"
                    disabled
                  />
                </div>
                <div>
                  <label className="form-label">ZIP Code *</label>
                  <input
                    type="text"
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="ZIP"
                    required
                    data-testid="zip-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Company Size</label>
                <select
                  name="company_size"
                  value={formData.company_size}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="company-size-select"
                >
                  <option value="">Select size</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-500">201-500 employees</option>
                  <option value="500+">500+ employees</option>
                </select>
              </div>

              {/* Contact Information */}
              <div style={{ gridColumn: '1 / -1', marginTop: '16px' }}>
                <h3 style={{ marginBottom: '16px', color: 'var(--primary-blue)' }}>Contact Information</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Contact Person Name *</label>
                <input
                  type="text"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Full name"
                  required
                  data-testid="contact-name-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="email@company.com"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="(901) 555-0123"
                  required
                  data-testid="phone-input"
                />
              </div>

              {/* Hiring Interests */}
              <div style={{ gridColumn: '1 / -1', marginTop: '16px' }}>
                <h3 style={{ marginBottom: '16px', color: 'var(--primary-blue)' }}>Hiring Interests</h3>
              </div>

              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">What positions are you looking to fill?</label>
                <textarea
                  name="hiring_needs"
                  value={formData.hiring_needs}
                  onChange={handleChange}
                  className="form-input"
                  rows={3}
                  placeholder="e.g., CMA positions, LPN positions, number of openings, shift preferences..."
                  data-testid="hiring-needs-input"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="ojt_interest"
                    checked={formData.ojt_interest}
                    onChange={handleChange}
                    style={{ width: '20px', height: '20px' }}
                    data-testid="ojt-checkbox"
                  />
                  <span>Interested in On-the-Job Training (OJT) Program</span>
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="post_training_interest"
                    checked={formData.post_training_interest}
                    onChange={handleChange}
                    style={{ width: '20px', height: '20px' }}
                    data-testid="post-training-checkbox"
                  />
                  <span>Interested in hiring graduates after training completion</span>
                </label>
              </div>

              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Additional Message (Optional)</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="form-input"
                  rows={3}
                  placeholder="Any additional information you'd like to share..."
                  data-testid="message-input"
                />
              </div>

              <div style={{ gridColumn: '1 / -1', marginTop: '16px' }}>
                <button 
                  type="submit" 
                  className="btn btn-primary btn-lg" 
                  style={{ width: '100%' }}
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? 'Submitting...' : 'Submit Employer Registration'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Contact Section */}
      <section className="info-section">
        <h2 className="section-title">Contact Our Employer Relations Team</h2>
        <div className="info-grid" style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div className="info-card" style={{ textAlign: 'center' }}>
            <Mail size={32} style={{ color: 'var(--primary-blue)', marginBottom: '12px' }} />
            <h3>Email Us</h3>
            <p><a href="mailto:medcare.nursing@yahoo.com">medcare.nursing@yahoo.com</a></p>
          </div>
          <div className="info-card" style={{ textAlign: 'center' }}>
            <Phone size={32} style={{ color: 'var(--primary-blue)', marginBottom: '12px' }} />
            <h3>Call Us</h3>
            <p><a href="tel:+19016251201">(901) 625-1201</a></p>
          </div>
          <div className="info-card" style={{ textAlign: 'center' }}>
            <MapPin size={32} style={{ color: 'var(--primary-blue)', marginBottom: '12px' }} />
            <h3>Visit Us</h3>
            <p>Memphis, Tennessee</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <p data-testid="footer-copyright">
            &copy; 2026 MedCare Nursing Institute &amp; Training Academy. All rights reserved.
          </p>
          <p>
            A program of the <strong>MedCare National Board of Nursing &amp; Vital Health Sciences Association</strong> —
            3150 E. Shelby Dr, Memphis TN 38118 · 901.625.1201
          </p>
          <p>Programs approved and registered by the TN Dept of Labor Workforce Development Apprenticeship Program.</p>
          <p>Contact: <a href="mailto:medcare.nursing@yahoo.com">medcare.nursing@yahoo.com</a></p>
        </div>
      </footer>
    </div>
  );
};

export default EmployerSignup;
